import * as React from "react";
import { apiPost, fetchData } from "../../../../common/services/ApiClient";
import {
  chunkArray,
  sortObjValue,
} from "../../../../common/services/Utilities";
import { ApiBaseUrl } from "../../../../config";
import { KeyValue, Query } from "../../../../interfaces";
import generateBranchOfficeDepositsApiUrl from "../../BranchOfficeDeposits/BranchOfficeDepositsUtilities";
import { removeDuplicates } from "./DepositMarketShareUtilities";
import DepositMarketTables from "./DepositMarketTables";

interface Props {
  appState: Query;
  searchPills: React.ReactElement;
  updatePagination: () => void;
}

export default function DepositMarketShareTable({
  appState,
  searchPills,
  updatePagination,
}: Props) {
  const [tableData, setTableData] = React.useState<KeyValue[]>([{}]);
  const [makeNoBHCCall, setMakeNoBHCCall] = React.useState(false);
  const [noBHCData, setNoBHCData] = React.useState<KeyValue[]>([{}]);
  const [ids, setIds] = React.useState<number[]>([]);
  const [getInMarketAgg, setGetInMarketAgg] = React.useState(false);
  const [getNoBHCAgg, setGetNoBHCAgg] = React.useState(false);

  const [makeOutMarketCall, setMakeOutMarketCall] = React.useState(false);
  const [totals, setTotals] = React.useState<KeyValue[]>([{}]);
  const [loading, setLoading] = React.useState(false);

  const [makeBhcInstitutionCall, setMakeBhcInstitutionCall] =
    React.useState(false);
  const [prevPagination, setPrevPagination] = React.useState(
    appState.pagination,
  );

  const [
    outsideSelectedMarketInstitutionData,
    setOutsideSelectedMarketInstitutionData,
  ] = React.useState<KeyValue>({});

  const [currentSubsidiary, setCurrentSubsidiary] = React.useState<number[]>(
    [],
  );
  const [subData, setSubData] = React.useState<KeyValue>({});
  const [makeOutMarketSubData, setMakeOutMarketSubData] = React.useState(false);

  const [makeInMarketSubsidiary, setMakeInMarketSubsidiary] =
    React.useState(false);
  const isBHC = appState.ui.institutionType === "bhcs";
  const key = isBHC && !getNoBHCAgg ? "RSSDHCR" : "CERT";

  const state = React.useMemo(() => {
    return {
      filters: appState.filters,
      search: appState.search,
      ui: {
        ...appState.ui,
        institutionTypeExpand: true,
        searchPillsHide: false,
      },
    };
  }, [appState.filters, appState.search, appState.ui]);
  const [previousState, setPreviousState] = React.useState({});

  const appstateFilter = appState.filters.msa
    ? appState.filters.msa
    : appState.filters.zipbr;
  const callFilter = appState.filters.msa ? " AND MSANAMB:" : " AND ZIPBR:";

  React.useEffect(() => {
    //Get In Market Institution or BHC Data
    if (JSON.stringify(previousState) !== JSON.stringify(state)) {
      setLoading(true);
      setPreviousState(state);
      let isMounted = true;

      const allInst =
        appState.filters.stateNames[0] &&
        appState.filters.stateNames[0] === "ALL";
      const additionalFilters = isBHC ? " AND !RSSDHCR:0" : "";
      const allApiCalls = {
        filters: `YEAR:${appState.filters.lastYear}${additionalFilters}`,
        get_unique: key,
        limit: 10000,
        offset: 0,
        fields:
          appState.ui.institutionType === "bhcs"
            ? "NAMEHCR,RSSDHCR,STALPHCR"
            : "BKCLASS,NAMEFULL,CHARTER,CERT,STNAMEBR,CNTYNAMB",
      };
      const selectedMarket = appState.filters.stateNames
        .filter((state: string) => state)
        .map((state: string, index: number) => {
          const additionalFilters = isBHC ? "!RSSDHCR:0" : "";
          const marketSelecion = appState.ui.marketSelection[index];
          const zips = chunkArray(marketSelecion?.zips, 500);
          return zips.map((arr) =>
            generateBranchOfficeDepositsApiUrl(
              appState,
              undefined,
              ...[
                state,
                { ...marketSelecion, zips: arr },
                key,
                additionalFilters,
              ],
            ),
          );
        });

      const marketApiCalls = allInst
        ? [apiPost(allApiCalls), apiPost({ ...allApiCalls, offset: 10000 })]
        : selectedMarket.flat();

      const apiCalls = appstateFilter
        ? [
            apiPost({
              filters: `YEAR:${appState.filters.lastYear}${additionalFilters}${callFilter}"${appstateFilter}"`,
              get_unique: key,
              limit: 10000,
              offset: 0,
              fields:
                appState.ui.institutionType === "bhcs"
                  ? "NAMEHCR,RSSDHCR,STALPHCR"
                  : "BKCLASS,NAMEFULL,CHARTER,CERT,STNAMEBR,CNTYNAMB,STALP",
            }),
          ]
        : marketApiCalls;
      (async () => {
        await Promise.all(
          apiCalls.map((post: any) => fetchData(`${ApiBaseUrl()}/sod`, post)),
        )
          .then((response) => {
            if (isMounted) {
              const combinedResponse = response
                .map((res: any) => res.data)
                .flat();
              const ids = combinedResponse.map((data) => data[key]);
              const uniqueIds = Array.from(new Set(ids));
              setTableData(removeDuplicates(combinedResponse, key));
              setIds(uniqueIds);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setGetInMarketAgg(true);
          });
      })();
    }
  }, [appstateFilter, callFilter, appState, isBHC, key, previousState, state]);

  React.useEffect(() => {
    //Get In Market Institution or BHC Data
    if (getInMarketAgg) {
      setGetInMarketAgg(false);
      let isMounted = true;
      const splitIds: any = chunkArray(ids, 500);
      const aggFetch = splitIds.map((idArray: number[]) => {
        const allCerts = ` AND ${key}:(${idArray.join(" OR ")})`;
        const additionalFilters =
          isBHC && !getNoBHCAgg
            ? `YEAR:${appState.filters.lastYear} AND !RSSDHCR:0`
            : getNoBHCAgg
              ? `YEAR:${appState.filters.lastYear} RSSDHCR:0`
              : `YEAR:${appState.filters.lastYear}`;
        let responses: any = [];

        if (
          appState.filters.stateNames[0] === "ALL" ||
          !appState.filters.stateNames[0]
        ) {
          responses = apiPost({
            agg_limit: 1000,
            offset: 0,
            agg_by: key,
            agg_sum_fields: "DEPSUMBR",
            filters: `YEAR:${appState.filters.lastYear}${allCerts}`,
          });
        }
        if (
          appState.filters.stateNames[0] &&
          appState.filters.stateNames[0] !== "ALL"
        ) {
          responses = appState.filters.stateNames
            .filter((state: string) => state)
            .map((state: string, index: number) => {
              if (appState.filters.stateNames[0] === "ALL") {
                return generateBranchOfficeDepositsApiUrl(
                  appState,
                  {
                    by: key,
                    limit: 10000,
                    sum: ["DEPSUMBR"],
                  },
                  state,
                  undefined,
                  key,
                  additionalFilters,
                );
              } else {
                const marketSelection = appState.ui.marketSelection[index];
                const zips = chunkArray(marketSelection?.zips, 500);
                const newurls = zips.map((arr) => {
                  return generateBranchOfficeDepositsApiUrl(
                    appState,
                    {
                      by: key,
                      limit: 10000,
                      sum: ["DEPSUMBR"],
                    },
                    state,
                    { ...marketSelection, zips: arr },
                    undefined,
                    `${additionalFilters}${allCerts}`,
                  );
                });
                return newurls;
              }
            });
        }
        if (appstateFilter) {
          responses = apiPost({
            agg_limit: 1000,
            offset: 0,
            agg_by: key,
            agg_sum_fields: "DEPSUMBR",
            filters: `YEAR:${appState.filters.lastYear}${allCerts}${callFilter}"${appstateFilter}"`,
          });
        }
        return responses;
      });
      (async () => {
        await Promise.all(
          aggFetch
            .flat(2)
            .map((post: any) => fetchData(`${ApiBaseUrl()}/sod`, post)),
        )
          .then((response) => {
            if (isMounted) {
              let inMarketCount = 0;
              let inMarketSum = 0;
              const combinedData = response.map((res: any) => res.data).flat();
              const combinedTotals = response
                .map((res: any) => res.totals)
                .flat()
                .filter((data) => data.count);

              const reducedData = combinedData.reduce((a, o) => {
                if (a[o[key]]) {
                  a[o[key]].count += o.count;
                  a[o[key]].sum_DEPSUMBR += o.sum_DEPSUMBR;
                } else {
                  a[o[key]] = o;
                }
                return a;
              }, {});

              combinedTotals.forEach((res: any) => {
                inMarketCount += res.count;
                inMarketSum += res.sum_DEPSUMBR;
              });

              !getNoBHCAgg &&
                setTotals([
                  {
                    NAMEFULL: "Totals",
                    count: inMarketCount,
                    sum_DEPSUMBR: inMarketSum,
                    count_out: 0,
                    sum_DEPSUMBR_out: 0,
                    mkt: 100,
                  },
                ]);

              const aggData = getNoBHCAgg ? noBHCData : tableData;
              const matchedResponses = aggData.map((data) => {
                const matchingMarketData = reducedData[data[key]];

                return {
                  ...data,
                  ...matchingMarketData,
                  count_out: 0,
                  sum_DEPSUMBR_out: 0,
                  mkt:
                    matchingMarketData?.sum_DEPSUMBR && inMarketSum
                      ? (Number(matchingMarketData.sum_DEPSUMBR) /
                          Number(inMarketSum)) *
                        100
                      : 0,
                };
              });

              const sortedTableData = sortObjValue(
                matchedResponses,
                "sum_DEPSUMBR",
                true,
              );

              getNoBHCAgg
                ? setNoBHCData(sortedTableData)
                : setTableData(sortedTableData);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setMakeOutMarketCall(true);
          });
      })();
    }
  }, [
    appstateFilter,
    callFilter,
    appState,
    getInMarketAgg,
    getNoBHCAgg,
    ids,
    isBHC,
    key,
    noBHCData,
    tableData,
  ]);

  React.useEffect(() => {
    //Get Out Market Data if required
    if (makeOutMarketCall) {
      let isMounted = true;
      setMakeOutMarketCall(false);
      if (ids.length || appState.filters.stateNames[0] !== "ALL") {
        const splitIds: any = chunkArray(ids, 500);
        const outMarketCalls = splitIds.map((dataArray: number[]) => {
          const allKeys = dataArray.length
            ? ` AND ${key}:(${dataArray.join(" OR ")})`
            : "";
          const additionalFilters =
            isBHC && !getNoBHCAgg
              ? " AND !RSSDHCR:0"
              : isBHC && getNoBHCAgg
                ? " AND RSSDHCR:0"
                : "";

          const responses = apiPost({
            agg_limit: 1000,
            offset: 0,
            agg_by: key,
            agg_sum_fields: "DEPSUMBR",
            filters: `YEAR:${appState.filters.lastYear}${allKeys}${additionalFilters}`,
          });

          return responses;
        });
        (async () => {
          await Promise.all(
            outMarketCalls.map((post: any) =>
              fetchData(`${ApiBaseUrl()}/sod`, post),
            ),
          )
            .then((response) => {
              if (isMounted) {
                const combinedRes = response.map((res) => res.data).flat();

                const outTotals = { count_out: 0, sum_DEPSUMBR_out: 0 };
                const outData = getNoBHCAgg ? noBHCData : tableData;
                const allData = outData.map((data: KeyValue) => {
                  const outMarketData = combinedRes.find(
                    (outMarket: KeyValue) => outMarket[key] === data[key],
                  );
                  const outCount = data?.count
                    ? outMarketData.count - data.count
                    : 0;
                  const outSum = outMarketData.sum_DEPSUMBR - data.sum_DEPSUMBR;
                  outTotals.count_out += outCount;
                  outTotals.sum_DEPSUMBR_out += outSum;

                  return {
                    ...data,
                    count_out: outCount,
                    sum_DEPSUMBR_out: outSum,
                  };
                });
                !getNoBHCAgg && setTotals([{ ...totals[0], ...outTotals }]);
                getNoBHCAgg ? setNoBHCData(allData) : setTableData(allData);
              }
            })

            .catch((error: Error) => {
              console.error(error);
            })
            .finally(() => {
              isMounted = false;
              isBHC && !getNoBHCAgg && setMakeNoBHCCall(true);
              !isBHC && setLoading(false);
              getNoBHCAgg && setGetNoBHCAgg(false);
            });
        })();
      } else {
        const noData = [
          { count: 0, count_out: 0, sum_DEPSUMBR: 0, sum_DEPSUMBR_out: 0 },
        ];
        getNoBHCAgg ? setNoBHCData(noData) : setTableData(noData);
        isBHC && !getNoBHCAgg && setMakeNoBHCCall(true);
        getNoBHCAgg && setGetNoBHCAgg(false);
      }
    }
  }, [
    appstateFilter,
    callFilter,
    appState.filters.lastYear,
    appState.filters.stateNames,
    getNoBHCAgg,
    ids,
    isBHC,
    key,
    makeOutMarketCall,
    noBHCData,
    tableData,
    totals,
  ]);

  React.useEffect(() => {
    if (makeNoBHCCall) {
      setMakeNoBHCCall(false);
      let isMounted = true;
      let apiCalls: any = [];
      if (
        appState.filters.stateNames[0] &&
        appState.filters.stateNames[0] === "ALL"
      ) {
        const allApiCalls = {
          filters: `YEAR:${appState.filters.lastYear} AND RSSDHCR:0`,
          get_unique: "CERT",
          limit: 10000,
          offset: 0,
          fields: "BKCLASS,NAMEFULL,CHARTER,CERT,STNAMEBR,CNTYNAMB,STALP",
        };
        apiCalls = [
          apiPost(allApiCalls),
          apiPost({ ...allApiCalls, offset: 10000 }),
        ];
      } else if (
        appState.filters.stateNames[0] &&
        appState.filters.stateNames[0] !== "ALL"
      ) {
        apiCalls = appState.filters.stateNames
          .filter((state: string) => state)
          .map((state: string, index: number) => {
            const additionalFilters = "RSSDHCR:0";
            const marketSelection = appState.ui.marketSelection[index];
            const zips = chunkArray(marketSelection?.zips, 500);
            return zips.map((arr) => {
              const allValues = [
                state,
                { ...marketSelection, zips: arr },
                "CERT",
                additionalFilters,
              ];
              return generateBranchOfficeDepositsApiUrl(
                appState,
                undefined,
                ...allValues,
              );
            });
          });
      } else if (appstateFilter) {
        apiCalls = [
          apiPost({
            filters: `YEAR:${appState.filters.lastYear} AND RSSDHCR:0${callFilter}"${appstateFilter}"`,
            get_unique: "CERT",
            limit: 10000,
            offset: 0,
            fields: "BKCLASS,NAMEFULL,CHARTER,CERT,STNAMEBR,CNTYNAMB,STALP",
          }),
        ];
      }

      (async () => {
        await Promise.all(
          apiCalls
            .flat()
            .map((post: any) => fetchData(`${ApiBaseUrl()}/sod`, post)),
        )
          .then((response) => {
            if (isMounted) {
              const flatResponse = response.map((res: any) => res.data).flat();
              const combinedResponse = removeDuplicates(flatResponse, "CERT");
              const ids = combinedResponse.map((data) => data.CERT);
              setNoBHCData(combinedResponse);
              const uniqueIds = Array.from(new Set(ids));
              setIds(uniqueIds);
              setGetNoBHCAgg(true);
              setMakeBhcInstitutionCall(true);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setGetInMarketAgg(true);
          });
      })();
    }
  }, [appstateFilter, callFilter, appState, isBHC, makeNoBHCCall]);

  React.useEffect(() => {
    if (
      makeBhcInstitutionCall ||
      JSON.stringify(prevPagination) !== JSON.stringify(appState.pagination)
    ) {
      setPrevPagination(appState.pagination);
      setMakeBhcInstitutionCall(false);

      const paginatedListRSSDHCR = tableData
        .slice(
          (appState.pagination.pageNumber - 1) *
            appState.pagination.resultLimit,
          appState.pagination.resultLimit * appState.pagination.pageNumber,
        )
        .map((data) => data.RSSDHCR);
      const filter = appState.filters.msa
        ? ` AND MSANAMB:"${appState.filters.msa}"`
        : appState.filters.zipbr
          ? ` AND ZIPBR:"${appState.filters.zipbr}"`
          : "";
      const fetches = paginatedListRSSDHCR.map((rssdhcr: number) => {
        const RSSDHCR = rssdhcr && `AND RSSDHCR:${rssdhcr}`;
        return fetchData(
          `${ApiBaseUrl()}/sod?filters=YEAR:${
            appState.filters.lastYear
          } ${RSSDHCR} AND !RSSDHCR:0${filter}&fields=BKCLASS,NAMEFULL,CHARTER,CERT,RSSDHCR&limit=10000&get_unique=CERT`,
        );
      });
      let isMounted = true;
      (async () => {
        await Promise.all(fetches)
          .then((response) => {
            if (isMounted) {
              const mergedResponse = response.map((res) => res.data).flat();
              const mergedWithTableData = tableData.map((tableData) => {
                const instRes = mergedResponse
                  .map((data) =>
                    data.RSSDHCR === tableData.RSSDHCR ? data : undefined,
                  )
                  .filter((res) => res);

                return { ...tableData, institutions: instRes };
              });
              setCurrentSubsidiary(
                mergedWithTableData
                  .map((data) => data.institutions.map((inst) => inst.CERT))
                  .flat(),
              );

              setTableData(mergedWithTableData);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setMakeInMarketSubsidiary(true);
          });
      })();
    }
  }, [
    appState.filters.lastYear,
    appState.filters.stateNames,
    appState.pagination,
    appState.filters.msa,
    appState.filters.zipbr,
    makeBhcInstitutionCall,
    prevPagination,
    tableData,
  ]);

  React.useEffect(() => {
    if (makeInMarketSubsidiary && currentSubsidiary.length && isBHC) {
      const additionalFilters = `CERT:(${currentSubsidiary.join(" OR ")})`;

      let apiCalls: any = [];
      if (appState.filters.stateNames[0]) {
        apiCalls = appState.filters.stateNames
          .filter((state: string) => state)
          .map((state: string, index: number) => {
            if (appState.filters.stateNames[0] === "ALL") {
              return generateBranchOfficeDepositsApiUrl(
                appState,
                {
                  by: "CERT",
                  limit: 10000,
                  sum: ["DEPSUMBR"],
                },
                state,
                undefined,
                key,
                additionalFilters,
              );
            } else {
              const marketSelection = appState.ui.marketSelection[index];
              const zips = chunkArray(marketSelection?.zips, 500);
              const newurls = zips.map((arr) => {
                return generateBranchOfficeDepositsApiUrl(
                  appState,
                  {
                    by: "CERT",
                    limit: 10000,
                    sum: ["DEPSUMBR"],
                  },
                  state,
                  { ...marketSelection, zips: arr },
                  undefined,
                  additionalFilters,
                );
              });
              return newurls;
            }
          });
      } else if (appstateFilter) {
        apiCalls = [
          apiPost({
            filters: `YEAR:${appState.filters.lastYear}${callFilter}"${appstateFilter}" AND ${additionalFilters}`,
            agg_by: "CERT",
            agg_limit: 10000,
            agg_sum_fields: "DEPSUMBR",
          }),
        ];
      }

      (async () => {
        await Promise.all(
          apiCalls
            .flat()
            .map((url: string) => fetchData(`${ApiBaseUrl()}/sod`, url)),
        )
          .then((response) => {
            const subData: KeyValue = {};
            const data = response.map((res) => res.data).flat();
            data.forEach((data: KeyValue) => {
              subData[data.CERT] = {
                count: data.count,
                sum_DEPSUMBR: data.sum_DEPSUMBR,
              };
            });
            setSubData(subData);
            setMakeInMarketSubsidiary(false);
            setMakeOutMarketSubData(true);
          })
          .catch((error: Error) => console.error(error));
      })();
    }
  }, [
    appstateFilter,
    callFilter,
    appState,
    appState.filters.lastYear,
    appState.filters.stateNames,
    currentSubsidiary,
    isBHC,
    key,
    makeInMarketSubsidiary,
  ]);

  React.useEffect(() => {
    if (
      makeOutMarketSubData &&
      currentSubsidiary.length > 0 &&
      isBHC &&
      Object.keys(subData).length > 0
    ) {
      const certFilter = ` AND CERT:(${currentSubsidiary.join(" OR ")})`;
      setMakeOutMarketSubData(false);
      let isMounted = true;

      (async () => {
        await Promise.resolve(
          fetchData(
            `${ApiBaseUrl()}/sod`,
            apiPost({
              filters: `YEAR:${appState.filters.lastYear}${certFilter}`,
              agg_by: "CERT",
              agg_limit: 10000,
              agg_sum_fields: "DEPSUMBR",
            }),
          )
            .then((response) => {
              if (isMounted) {
                const outData: KeyValue = {};
                response.data.forEach((data: KeyValue) => {
                  if (subData.hasOwnProperty(data.CERT)) {
                    outData[data.CERT] = {
                      count: data.count - subData[data.CERT].count,
                      sum_DEPSUMBR:
                        data.sum_DEPSUMBR - subData[data.CERT].sum_DEPSUMBR,
                    };
                  }
                });
                setOutsideSelectedMarketInstitutionData(outData);
              }
            })
            .catch((error: Error) => {
              console.error(error);
            })
            .finally(() => {
              setLoading(false);
              isMounted = false;
            }),
        );
      })();
    }
  }, [
    appState.filters.lastYear,
    currentSubsidiary,
    isBHC,
    makeOutMarketSubData,
    subData,
    appState.filters.msa,
    appState.filters.zipbr,
  ]);
  return (
    <DepositMarketTables
      tableData={tableData}
      noBHCData={noBHCData}
      tableDataTotals={totals}
      tableComponent={"territories"}
      appState={appState}
      searchPills={searchPills}
      loading={loading}
      updatePagination={updatePagination}
      subData={subData}
      outsideSelectedMarketInstitutionData={
        outsideSelectedMarketInstitutionData
      }
    />
  );
}
